import { fbt } from 'fbtee'
import { Lock } from 'lucide-react'
import { SetInterestIcon } from './SetInterestIcon'
import { Dialog, DialogContent, DialogTitle } from './ui/dialog'
import { Button } from './ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs'
import { SetDetailArtistSection, type ArtistHeart } from './SetDetailArtistSection'
import { ArtistGenreEyebrow } from './ArtistGenreEyebrow'
import { MemberInterestList, type TotemDisplay } from './MemberInterestList'
import { SetPostComposer } from './SetPostComposer'
import { SetPostItem } from './SetPostItem'
import { EmptyState } from './EmptyState'
import { splitPanelStyle } from '../lib/splitPanel'
import { cn } from '../lib/utils'
import { useSuppressSelectionOnOpen } from '../hooks/useSuppressSelectionOnOpen'
import type { CreateSetPostInput } from '../hooks/useSetPosts'
import type { Artist, Group, PartialRange, SetPost, UserProfile } from '../types'
import { SetCopySection } from './SetCopySection'
import { focalPosition } from '../lib/focalPosition'
import { SetReportButton } from './SetReportButton'

export type SetDetailTab = 'details' | 'feed' | 'people'

const EMPTY_IDS: string[] = []

// Guests can browse a schedule, but who's going and the set feed are member-only.
// Lock those tab bodies behind a sign-in prompt rather than showing the details.
function LockedTab({ message, onRequireAuth }: { message: React.ReactNode; onRequireAuth: () => void }) {
  return (
    <div className="flex flex-col items-center gap-3 py-12 text-center">
      <Lock className="size-6 text-muted-foreground" aria-hidden />
      <p className="text-sm text-muted-foreground max-w-[16rem]">{message}</p>
      <Button size="sm" onClick={onRequireAuth} className="cursor-pointer">
        <fbt desc="Button to log in / sign up from a locked set tab">Log in</fbt>
      </Button>
    </div>
  )
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  tab: SetDetailTab
  onTabChange: (tab: SetDetailTab) => void
  name?: string
  subtitle?: string
  artists?: Artist[]
  /** Whose photos fill the cover, when not `artists` — a named collab's own photo. */
  coverArtists?: Artist[]
  /** Shown above the artist sections, without a heading of its own — a named collab's bio. */
  intro?: React.ReactNode
  titled?: boolean
  /** The set doc has no artistIds — gates the slot's own copy (description, note). */
  artistless?: boolean
  description?: string
  note?: string
  placeholder?: boolean
  setId?: string
  artistId?: string
  /** A set (not an artist's lineup entry) shows "Report a problem". */
  reportSet?: { id: string; eventId: string; artistIds: string[] }
  interestedMemberIds: string[]
  goingMemberIds: string[]
  currentUserStatus: 'interested' | 'going' | null
  posts: SetPost[]
  profiles: Record<string, UserProfile>
  currentUserId?: string | null
  totems?: TotemDisplay[]
  totemAction?: React.ReactNode
  setStartTime?: string
  setEndTime?: string
  onUpdatePartialRange?: (
    groupId: string,
    totemId: string,
    partialRange: PartialRange | null,
  ) => void
  /** The viewer's own slice of this set, editable from their row on the People tab. */
  ownPartialRange?: PartialRange | null
  onUpdateOwnRange?: (partialRange: PartialRange | null) => void
  memberRanges?: Record<string, PartialRange>
  /** Festival mode: people checked in at this set. */
  checkedInIds?: ReadonlySet<string>
  onOpenTotem?: (groupId: string, totemId: string) => void
  onOpenMember?: (uid: string) => void
  groups: Group[]
  isGuest: boolean
  onRequireAuth: () => void
  onToggle?: () => void
  onCreatePost: (input: CreateSetPostInput) => Promise<void>
  onDeletePost: (postId: string) => void
  /** A lineup act with several performers: no feed (posts are per artist), a heart per artist instead. */
  hideFeed?: boolean
  artistHeart?: (artistId: string) => ArtistHeart | undefined
}

export function SetDetailDialog({
  open,
  onOpenChange,
  tab,
  onTabChange,
  name,
  subtitle,
  artists,
  coverArtists,
  intro,
  titled,
  artistless,
  description,
  note,
  placeholder,
  setId,
  artistId,
  reportSet,
  interestedMemberIds,
  goingMemberIds,
  currentUserStatus,
  posts,
  profiles,
  currentUserId,
  totems,
  totemAction,
  setStartTime,
  setEndTime,
  onUpdatePartialRange,
  ownPartialRange,
  onUpdateOwnRange,
  memberRanges,
  checkedInIds,
  onOpenTotem,
  onOpenMember,
  groups,
  isGuest,
  onToggle,
  onRequireAuth,
  onCreatePost,
  onDeletePost,
  hideFeed = false,
  artistHeart,
}: Props) {
  const suppressSelect = useSuppressSelectionOnOpen(open)

  const interested =
    currentUserId && currentUserStatus === 'interested'
      ? [currentUserId, ...interestedMemberIds]
      : interestedMemberIds
  const going =
    currentUserId && currentUserStatus === 'going'
      ? [currentUserId, ...goingMemberIds]
      : goingMemberIds
  const peopleCount = interested.length + going.length

  const hasArtists = !!(artists && artists.length > 0)
  const isB2b = hasArtists && artists.length > 1
  const showArtistHeadings = isB2b || (!!titled && hasArtists)
  const cover = coverArtists ?? artists ?? []
  const checkedIn = !!(currentUserId && checkedInIds?.has(currentUserId))
  const hasAnyImage = cover.some(a => a.imageUrl)

  // Gate on the set doc, not resolved artists: artist docs may still be loading or missing.
  const displayNote = artistless ? note?.trim() || undefined : undefined
  const slotCopy = artistless ? description?.trim() || undefined : undefined
  const hasSetCopy = Boolean(slotCopy || displayNote || (artistless && placeholder))
  const showDetails = hasArtists || hasSetCopy
  // Callers default to 'details'; an artist-less set with no copy has no Details panel.
  const activeTab: SetDetailTab =
    tab === 'details' && artistless && !hasSetCopy ? 'feed' : tab === 'feed' && hideFeed ? 'details' : tab
  const reportLink = reportSet && (
    <div className="pt-2">
      <SetReportButton set={reportSet} setName={name ?? fbt('Set', 'Fallback set detail dialog title')} />
    </div>
  )

  const coverImage = hasAnyImage ? (
    <div className="relative h-40 shrink-0 bg-card">
      {cover.length === 1 ? (
        cover[0].imageUrl && (
          <div
            className="absolute inset-0 bg-cover"
            style={{ backgroundImage: `url(${cover[0].imageUrl})`, backgroundPosition: focalPosition(cover[0], '50% 25%') }}
          />
        )
      ) : (
        cover.map((artist, i) =>
          artist.imageUrl ? (
            <div
              key={artist.id}
              className="absolute inset-y-0"
              style={{
                ...splitPanelStyle(i, cover.length),
                backgroundImage: `url(${artist.imageUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: focalPosition(artist, 'center 25%'),
              }}
            />
          ) : null,
        )
      )}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-popover/40 to-popover" />
      <div className="absolute inset-x-0 bottom-0 p-4 pb-3 flex items-end gap-3">
        <div className="flex-1 min-w-0 space-y-0.5">
          <ArtistGenreEyebrow artists={artists} />
          <DialogTitle className="text-lg font-semibold line-clamp-2 break-words leading-snug text-foreground">
            {name ?? fbt('Set', 'Fallback set detail dialog title')}
          </DialogTitle>
          {subtitle && <p className="text-sm text-muted-foreground truncate">{subtitle}</p>}
        </div>
        <div className="shrink-0 flex items-center">
          {totemAction}
          {onToggle && (
            <button
              type="button"
              onClick={onToggle}
              className="shrink-0 cursor-pointer p-1.5"
              aria-label={fbt('Toggle interest', 'Accessible label for the set interest toggle in the detail dialog')}
            >
              <SetInterestIcon status={currentUserStatus} checkedIn={checkedIn} idleClassName="text-on-image/80" />
            </button>
          )}
        </div>
      </div>
    </div>
  ) : (
    <div className="pl-4 pr-12 pt-4 pb-3 shrink-0 flex items-start gap-3">
      <div className="flex-1 min-w-0 space-y-0.5">
        <ArtistGenreEyebrow artists={artists} />
        <DialogTitle className="text-lg font-semibold break-words leading-snug text-foreground">
          {name ?? fbt('Set', 'Fallback set detail dialog title')}
        </DialogTitle>
        {subtitle && <p className="text-sm text-muted-foreground truncate">{subtitle}</p>}
      </div>
      <div className="shrink-0 flex items-center">
        {totemAction}
        {onToggle && (
          <button
            type="button"
            onClick={onToggle}
            className="shrink-0 cursor-pointer p-1 mt-0.5"
            aria-label={fbt('Toggle interest', 'Accessible label for the set interest toggle in the detail dialog')}
          >
            <SetInterestIcon status={currentUserStatus} checkedIn={checkedIn} idleClassName="text-muted-foreground" />
          </button>
        )}
      </div>
    </div>
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        aria-describedby={undefined}
        className={cn(
          'p-0 gap-0 overflow-hidden flex flex-col max-h-[75vh] top-[calc(10vh+env(safe-area-inset-top))] translate-y-0',
          suppressSelect && 'select-none [-webkit-touch-callout:none]',
        )}
      >
        {coverImage}

        <Tabs
          value={activeTab}
          onValueChange={v => onTabChange(v as SetDetailTab)}
          className="flex-1 min-h-0 flex flex-col"
        >
          <TabsList className="w-full shrink-0 mx-4 mt-2" style={{ width: 'calc(100% - 2rem)' }}>
            {showDetails && (
              <TabsTrigger value="details">
                {fbt('Details', 'Tab label for the set artist details')}
              </TabsTrigger>
            )}
            {!hideFeed && (
              <TabsTrigger value="feed">
                {fbt('Feed', 'Tab label for the set feed (comments + media)')}
                {posts.length > 0 && <span className="ml-1 tabular-nums text-muted-foreground">{posts.length}</span>}
              </TabsTrigger>
            )}
            <TabsTrigger value="people">
              {fbt('People', "Tab label for the set's interested/going members")}
              {peopleCount > 0 && <span className="ml-1 tabular-nums text-muted-foreground">{peopleCount}</span>}
            </TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-y-auto">
            {showDetails && (
              <TabsContent value="details" className="p-4 space-y-4">
                {hasSetCopy && <SetCopySection description={slotCopy} note={displayNote} placeholder={placeholder} />}
                {intro}
                {artists?.map((artist, i) => (
                  <div key={artist.id}>
                    {showArtistHeadings && (i > 0 || !!intro) && <div className="border-t border-border mb-4" />}
                    <SetDetailArtistSection artist={artist} showHeading={showArtistHeadings} showGenre={isB2b} heart={artistHeart?.(artist.id)} />
                  </div>
                ))}
                {reportLink}
              </TabsContent>
            )}

            {!hideFeed && <TabsContent value="feed" className="p-4 space-y-4">
              {isGuest ? (
                <LockedTab
                  message={fbt(
                    'Log in to see the feed and hype this set with your crew.',
                    'Locked-tab message shown to guests on the set feed',
                  )}
                  onRequireAuth={onRequireAuth}
                />
              ) : (
                <>
                  <SetPostComposer
                    groups={groups}
                    isGuest={isGuest}
                    onRequireAuth={onRequireAuth}
                    onSubmit={input => onCreatePost({ ...input, ...(setId ? { setId } : { artistId }) })}
                  />
                  {posts.length === 0 ? (
                    <EmptyState>
                      {fbt(
                        'No posts yet. Be the first to hype this set.',
                        'Empty state for a set feed with no posts yet',
                      )}
                    </EmptyState>
                  ) : (
                    <ul className="flex flex-col gap-4 max-h-[50vh] overflow-y-auto">
                      {posts.map(post => (
                        <SetPostItem
                          key={post.id}
                          post={post}
                          profile={profiles[post.authorId]}
                          isAuthor={post.authorId === currentUserId}
                          onDelete={() => onDeletePost(post.id)}
                        />
                      ))}
                    </ul>
                  )}
                </>
              )}
              {!showDetails && reportLink}
            </TabsContent>}

            <TabsContent value="people" className="p-4">
              {isGuest ? (
                // Totems are public meetup markers a guest may follow, so keep
                // them visible; only the "who's going" member breakdown is locked.
                <div className="space-y-4">
                  {totems && totems.length > 0 && (
                    <MemberInterestList
                      interestedMemberIds={EMPTY_IDS}
                      goingMemberIds={EMPTY_IDS}
                      profiles={profiles}
                      currentUserId={currentUserId}
                      totems={totems}
                      setStartTime={setStartTime}
                      setEndTime={setEndTime}
                      onOpenTotem={onOpenTotem}
                      onOpenMember={onOpenMember}
                    />
                  )}
                  <LockedTab
                    message={fbt(
                      "Log in to see who's going.",
                      'Locked-tab message shown to guests on the set people list',
                    )}
                    onRequireAuth={onRequireAuth}
                  />
                </div>
              ) : peopleCount === 0 && (!totems || totems.length === 0) ? (
                <EmptyState>
                  {fbt(
                    "No one's marked this set yet.",
                    'Empty state for a set with no interested members',
                  )}
                </EmptyState>
              ) : (
                <MemberInterestList
                  interestedMemberIds={interested}
                  goingMemberIds={going}
                  profiles={profiles}
                  currentUserId={currentUserId}
                  totems={totems}
                  setStartTime={setStartTime}
                  setEndTime={setEndTime}
                  onUpdatePartialRange={onUpdatePartialRange}
                  ownPartialRange={ownPartialRange}
                  onUpdateOwnRange={onUpdateOwnRange}
                  memberRanges={memberRanges}
                  checkedInIds={checkedInIds}
                  onOpenTotem={onOpenTotem}
                  onOpenMember={onOpenMember}
                />
              )}
            </TabsContent>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}
